import React, { useState, useEffect } from 'react';

import {
  Box,
  Text,
  Input,
  VStack,
  HStack,
  Button,
  Grid,
  GridItem,
  IconButton,
  Collapse,
} from '@chakra-ui/react';

import { AddIcon, CloseIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import { useColors } from './colors';

const SpecificationItem = ({
  spec,
  index,
  isAmazon,
  updateSpecification,
  removeSpecification,
  inputColor,
  placeholderColor,
  iconBgColor,
}) => {
  if (isAmazon) {
    return (
      <HStack spacing={2} align="flex-start">
        <Input
          value={spec.bulletPoint}
          onChange={(e) => updateSpecification(spec.id, 'bulletPoint', e.target.value)}
          placeholder={`Bullet point ${index + 1}...`}
          size="md"
          flex="1"
          _focus={{
            borderColor: 'blue.400',
            boxShadow: '0 0 0 1px #3182ce',
          }}
          color={inputColor}
          _placeholder={{ color: placeholderColor }}
        />
        <IconButton
          size="sm"
          variant="ghost"
          icon={<CloseIcon boxSize={3} />}
          onClick={() => removeSpecification(spec.id)}
          aria-label="Remove Bullet Point"
          bg={iconBgColor}
        />
      </HStack>
    );
  }

  return (
    <HStack spacing={2} align="flex-start">
      <Input
        value={spec.key}
        onChange={(e) => updateSpecification(spec.id, 'key', e.target.value)}
        placeholder="Key..."
        size="md"
        flex="1"
        _focus={{
          borderColor: 'blue.400',
          boxShadow: '0 0 0 1px #3182ce',
        }}
        color={inputColor}
        _placeholder={{ color: placeholderColor }}
      />
      <Input
        value={spec.value}
        onChange={(e) => updateSpecification(spec.id, 'value', e.target.value)}
        placeholder="Value..."
        size="md"
        flex="1"
        _focus={{
          borderColor: 'blue.400',
          boxShadow: '0 0 0 1px #3182ce',
        }}
        color={inputColor}
        _placeholder={{ color: placeholderColor }}
      />
      <IconButton
        size="sm"
        variant="ghost"
        icon={<CloseIcon boxSize={3} />}
        onClick={() => removeSpecification(spec.id)}
        aria-label="Remove Specific"
        bg={iconBgColor}
      />
    </HStack>
  );
};

const ItemSpecifications = ({ listing, onUpdate }) => {
  const [specifications, setSpecifications] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const {
    bgColor,
    borderColor,
    headingColor,
    subTextColor: labelColor,
    iconBgColor,
    inputColor,
    placeholderColor,
  } = useColors();

  const isAmazon = listing?.marketplace?.name?.toLowerCase() === 'amazon';
  const maxItems = isAmazon ? 10 : Infinity;

  // Initialize specifications from listing data
  useEffect(() => {
    if (listing && !hasInitialized) {
      if (isAmazon && listing.marketplaceDetails?.bulletPoints) {
        // Amazon uses bullet points
        const bulletSpecs = listing.marketplaceDetails.bulletPoints.map(
          (bulletPoint, index) => ({
            id: Date.now() + index,
            bulletPoint,
          }),
        );
        setSpecifications(bulletSpecs);
        // Set initial expanded state: collapsed if there are specs, expanded if none
        setIsExpanded(bulletSpecs.length === 0);
      } else if (!isAmazon && listing.marketplaceDetails?.itemSpecifics) {
        // eBay uses item specifics (key-value pairs)
        const itemSpecs = Object.entries(listing.marketplaceDetails.itemSpecifics).map(
          ([key, value], index) => ({
            id: Date.now() + index,
            key,
            value,
          }),
        );
        setSpecifications(itemSpecs);
        // Set initial expanded state: collapsed if there are specs, expanded if none
        setIsExpanded(itemSpecs.length === 0);
      } else {
        // No specs found, start expanded
        setIsExpanded(true);
      }
      setHasInitialized(true);
    }
  }, [listing, isAmazon, hasInitialized]);

  // Calculate items per column based on total count
  const getItemsPerColumn = () => {
    const totalCount = specifications.length;
    if (totalCount <= 10) return 5;
    if (totalCount <= 20) return 10;
    return Math.ceil(totalCount / 2);
  };

  const itemsPerColumn = getItemsPerColumn();

  const addSpecification = () => {
    if (isAmazon && specifications.length >= maxItems) return;

    const newSpec = isAmazon
      ? { id: Date.now(), bulletPoint: '' }
      : { id: Date.now(), key: '', value: '' };

    const newSpecs = [...specifications, newSpec];
    setSpecifications(newSpecs);

    // Format data for API
    const formattedData = formatSpecificationsForAPI(newSpecs);
    onUpdate?.(formattedData);
  };

  const removeSpecification = (id) => {
    const newSpecs = specifications.filter((spec) => spec.id !== id);
    setSpecifications(newSpecs);

    // Format data for API
    const formattedData = formatSpecificationsForAPI(newSpecs);
    onUpdate?.(formattedData);
  };

  const updateSpecification = (id, field, value) => {
    const newSpecs = specifications.map((spec) =>
      spec.id === id ? { ...spec, [field]: value } : spec,
    );
    setSpecifications(newSpecs);

    // Format data for API
    const formattedData = formatSpecificationsForAPI(newSpecs);
    onUpdate?.(formattedData);
  };

  // Format specifications for API based on marketplace
  const formatSpecificationsForAPI = (specs) => {
    if (isAmazon) {
      // Amazon uses bulletPoints array
      return {
        bulletPoints: specs
          .filter((spec) => spec.bulletPoint && spec.bulletPoint.trim())
          .map((spec) => spec.bulletPoint.trim()),
      };
    } else {
      // eBay uses itemSpecifics object
      const itemSpecifics = {};
      specs.forEach((spec) => {
        if (spec.key && spec.key.trim() && spec.value && spec.value.trim()) {
          itemSpecifics[spec.key.trim()] = spec.value.trim();
        }
      });
      return { itemSpecifics };
    }
  };

  // Split specifications into two columns
  const leftColumnSpecs = specifications.slice(0, itemsPerColumn);
  const rightColumnSpecs = specifications.slice(itemsPerColumn);

  const hasContent = specifications.length > 0;

  return (
    <Box
      bg={bgColor}
      borderRadius="xl"
      p={6}
      border="1px solid"
      borderColor={borderColor}
    >
      <HStack justify="space-between" align="center" mb={6}>
        <HStack spacing={3}>
          <Text fontSize="xl" fontWeight="semibold" color={headingColor}>
            Item Specifics {isAmazon ? '/ Bullet Points' : ''}
          </Text>
          {hasContent && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
              onClick={() => setIsExpanded(!isExpanded)}
              color={labelColor}
            >
              {isExpanded ? 'Hide' : 'Show'} ({specifications.length})
            </Button>
          )}
        </HStack>
        <Button
          leftIcon={<AddIcon />}
          size="sm"
          colorScheme="gray.200"
          variant="outline"
          onClick={addSpecification}
          isDisabled={isAmazon && specifications.length >= maxItems}
        >
          Add {isAmazon ? 'bullet Point' : 'Item Specific'}
        </Button>
      </HStack>

      {isAmazon && (
        <Text fontSize="sm" color={labelColor} mb={4}>
          Maximum {maxItems} bullet points allowed
        </Text>
      )}

      <Collapse in={isExpanded} animateOpacity>
        <Grid templateColumns="1fr 1fr" gap={6}>
          {/* Left Column */}
          <GridItem>
            <VStack spacing={3} align="stretch">
              {leftColumnSpecs.map((spec, index) => (
                <SpecificationItem
                  key={spec.id}
                  spec={spec}
                  index={index}
                  isAmazon={isAmazon}
                  updateSpecification={updateSpecification}
                  removeSpecification={removeSpecification}
                  inputColor={inputColor}
                  placeholderColor={placeholderColor}
                  iconBgColor={iconBgColor}
                />
              ))}
            </VStack>
          </GridItem>

          {/* Right Column */}
          <GridItem>
            <VStack spacing={3} align="stretch">
              {rightColumnSpecs.map((spec, index) => (
                <SpecificationItem
                  key={spec.id}
                  spec={spec}
                  index={index + itemsPerColumn}
                  isAmazon={isAmazon}
                  updateSpecification={updateSpecification}
                  removeSpecification={removeSpecification}
                  inputColor={inputColor}
                  placeholderColor={placeholderColor}
                  iconBgColor={iconBgColor}
                />
              ))}
            </VStack>
          </GridItem>
        </Grid>

        {specifications.length === 0 && (
          <Box textAlign="center" py={8}>
            <Text color={labelColor} fontSize="sm">
              No {isAmazon ? 'bullet points' : 'item specifics'} found for this listing.
            </Text>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default ItemSpecifications;
